import { useRef, useState, Fragment } from 'react';
import axios from 'axios';
import { Camera, Upload, X, Loader2, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import Modal from './Modal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * OcrCapture — snap or upload a photo of handwritten records. The image is sent to the
 * backend (Gemini 3 Flash vision) which returns structured rows for the user to review
 * before bulk-saving via the parent-supplied onSubmit handler.
 *
 * Props:
 *   show, onClose, title, valueLabel, mode ("bags" | "harvest"), onSubmit
 */
const OcrCapture = ({ show, onClose, title = 'Capture from Photo', valueLabel = 'Value', mode, onSubmit }) => {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setImage(null); setRows([]); setBusy(false); setSubmitting(false); };
  const handleClose = () => { reset(); onClose && onClose(); };

  // Read the file, downscale to keep payload small, and return {base64, mime}
  const prepareImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxSide = 1600;
        let { width, height } = img;
        if (width > maxSide || height > maxSide) {
          const scale = Math.min(maxSide / width, maxSide / height);
          width = Math.round(width * scale); height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        resolve({ base64: dataUrl.split(',')[1], mime: 'image/jpeg', preview: dataUrl });
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const runOcr = async (file) => {
    if (!file) return;
    setBusy(true); setRows([]);
    try {
      const { base64, mime, preview } = await prepareImage(file);
      setImage(preview);
      const { data } = await axios.post(`${API}/api/ocr/extract`, {
        image_base64: base64, mime_type: mime, mode,
      }, { withCredentials: true });
      const detected = data?.rows || [];
      if (detected.length === 0) {
        toast.warning('No rows detected. Try a clearer photo or add rows manually.');
        setRows([{ bag_id: '', value: '' }]);
      } else {
        toast.success(`${detected.length} row(s) detected — review before saving`);
        setRows(detected.map(r => ({ bag_id: String(r.bag_id ?? ''), value: String(r.value ?? '') })));
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'OCR failed. Please try again.');
      setRows([{ bag_id: '', value: '' }]);
    } finally { setBusy(false); }
  };

  const updateRow = (i, field, val) => {
    const copy = [...rows]; copy[i] = { ...copy[i], [field]: val }; setRows(copy);
  };
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const addBlankRow = () => setRows([...rows, { bag_id: '', value: '' }]);

  const handleSave = async () => {
    const clean = rows
      .map(r => ({ bag_id: (r.bag_id || '').trim(), value: (r.value || '').trim() }))
      .filter(r => r.bag_id && r.value && !isNaN(parseFloat(r.value)));
    if (clean.length === 0) { toast.error('Fill at least one valid row'); return; }
    setSubmitting(true);
    try {
      await onSubmit(clean);
      reset(); onClose && onClose();
    } catch (err) {
      // errors surfaced by onSubmit
    } finally { setSubmitting(false); }
  };

  return (
    <Modal show={show} onClose={handleClose} title={title} testId="ocr-capture-modal">
      <div className="space-y-4">
        <p className="text-xs text-[#5C6B63]">Snap a photo of your handwritten sheet or upload an image. Our AI will extract Bag ID + {valueLabel} rows for you to review before saving.</p>

        {!image && (
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => camRef.current?.click()} className="flex flex-col items-center gap-1 border-2 border-dashed border-[#2C4A3B] rounded-lg py-6 text-[#2C4A3B] hover:bg-[#EDF5EE] font-medium text-sm" data-testid="ocr-open-camera">
              <Camera className="w-6 h-6" />
              Snap photo
            </button>
            <button type="button" onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-1 border-2 border-dashed border-[#5C6B63] rounded-lg py-6 text-[#5C6B63] hover:bg-[#F7F5F0] font-medium text-sm" data-testid="ocr-open-file">
              <Upload className="w-6 h-6" />
              Upload image
            </button>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => e.target.files?.[0] && runOcr(e.target.files[0])} data-testid="ocr-camera-input" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && runOcr(e.target.files[0])} data-testid="ocr-file-input" />
          </div>
        )}

        {image && (
          <div className="relative">
            <img src={image} alt="Captured" className="w-full max-h-60 object-contain rounded-lg border border-[#E4DFD3] bg-[#F7F5F0]" />
            <button type="button" onClick={reset} className="absolute top-2 right-2 bg-white/90 hover:bg-white p-1 rounded shadow" data-testid="ocr-reset"><X className="w-4 h-4 text-[#D05A4A]" /></button>
          </div>
        )}
        {busy && (
          <div className="flex items-center gap-2 text-sm text-[#5C6B63]" data-testid="ocr-progress">
            <Loader2 className="w-4 h-4 animate-spin text-[#2C4A3B]" />
            Reading your handwriting…
          </div>
        )}

        {rows.length > 0 && !busy && (
          <div className="space-y-2" data-testid="ocr-rows">
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#5C6B63]">Detected {rows.length} row(s). Fix any mistakes below then save.</p>
              <button type="button" onClick={addBlankRow} className="text-xs text-[#2C4A3B] hover:underline flex items-center gap-1" data-testid="ocr-add-row"><Plus className="w-3 h-3" /> Add row</button>
            </div>
            <div className="grid grid-cols-[1fr,1fr,auto] gap-2 items-center max-h-[38vh] overflow-y-auto pr-1">
              <span className="text-[10px] uppercase tracking-wider text-[#5C6B63] font-semibold">Bag ID</span>
              <span className="text-[10px] uppercase tracking-wider text-[#5C6B63] font-semibold">{valueLabel}</span>
              <span></span>
              {rows.map((r, i) => (
                <Fragment key={i}>
                  <input value={r.bag_id} onChange={e => updateRow(i, 'bag_id', e.target.value)} placeholder="Bag ID" className="w-full rounded-md border border-[#E4DFD3] bg-white px-2 py-1.5 text-sm" data-testid={`ocr-bag-${i}`} />
                  <input value={r.value} onChange={e => updateRow(i, 'value', e.target.value)} placeholder={valueLabel} type="number" step="0.01" className="w-full rounded-md border border-[#E4DFD3] bg-white px-2 py-1.5 text-sm" data-testid={`ocr-value-${i}`} />
                  <button type="button" onClick={() => removeRow(i)} className="p-1 hover:bg-[#FAEDED] rounded" data-testid={`ocr-remove-${i}`}><Trash2 className="w-3.5 h-3.5 text-[#D05A4A]" /></button>
                </Fragment>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={handleSave} disabled={rows.length === 0 || submitting || busy} className="flex-1 bg-[#2C4A3B] hover:bg-[#3E6652] text-white rounded-md px-4 py-2 font-medium disabled:opacity-50" data-testid="ocr-save-all">{submitting ? 'Saving…' : `Save ${rows.length || ''} rows`}</button>
          <button type="button" onClick={handleClose} className="flex-1 border border-[#E4DFD3] text-[#2C4A3B] rounded-md px-4 py-2 font-medium" data-testid="ocr-cancel">Cancel</button>
        </div>
      </div>
    </Modal>
  );
};

export default OcrCapture;
